import withRouter from "Components/Common/withRouter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { EmployeesSliceState, setEmployeeCount, setEmployeeQuery, setEmployees, setIsLoadingEmployees } from "slices/human-resources/employees/reducer"
import { HumanResourcesSliceState, setShowSyncModal } from "slices/human-resources/reducer"
import { syncDayforceData } from "slices/human-resources/thunk"
import useSyncProcess from "hooks/useSyncProcess"
import { deleteConsultant, getEmployeeFormFieldValues, getEmployees, getMostRecentSyncLog } from "slices/thunk"
import { downloadEmployeesCsv } from "slices/human-resources/employees/thunk"
import Employee, { DEFAULT_EMPLOYEE_QUERY, IEmployeeQueryParams } from 'models/employee';
import { navigateToCreateConsultants, navigateToEditConsultants, navigateToEmployeeDetails, navigateToManual } from "utils/navigator"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import { SelectOption } from "Components/molecule/inputs/select-input"
import capitalize from "utils/capitalize"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchEmployeesForm from "Components/organisms/forms/search-employee-form"
import ActionButton from "Components/atoms/buttons/action-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import SyncButton from "Components/molecule/sync-button"
import { SyncLogTypes } from "models/sync-log"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { employeeBaseColumns } from "data/columns/employee-column-set"
import Modal from "Components/molecule/modal"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import { Status } from "models/org-chart-node"
import Screen, { SCREENS } from "models/screen"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { EmploymentStatuses } from "models/employment-status"
import { PlusIcon, HelpIcon } from "Components/atoms/icons"
import { toDepartmentOptions } from "utils/department-options"

const getDefaultQuery = (statuses: Status[]): IEmployeeQueryParams => {
    const activeStatus = statuses.find(s => s.description === EmploymentStatuses.ACTIVE);
    return {
        ...DEFAULT_EMPLOYEE_QUERY,
        statusId: activeStatus?.id ?? ''
    }
}

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
)

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({
        isSyncingData,
        showSyncModal,
    }: HumanResourcesSliceState) => ({
        isSyncingData,
        showSyncModal,
    })
)

const selectEmployee = createSelector(
    (state: any) => state.Employees,
    ({
        employees,
        isLoadingEmployees,
        employeeCount,
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        query
    }: EmployeesSliceState) => ({
        employees,
        isLoadingEmployees,
        employeeCount,
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        query
    })
)


const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)


const EmployeeListPage = (props: any) => {
    document.title = "Employees | IbexIQ";
    const [isExportingData, setIsExportingData] = useState<boolean>(false);
    const [isDeletingEmployee, setIsDeletingEmployee] = useState<boolean>(false);
    const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState<boolean>(false);
    const [showDeleteConsultantModal, setShowDeleteConsultantModel] = useState<boolean>(false);
    const [focusedEmployee, setFocusedEmployee] = useState<Employee | null>(null);
    const dispatch: any = useDispatch();

    const {
        showSyncModal,
    } = useSelector(selectHumanResources);

    const {
        employees,
        isLoadingEmployees,
        employeeCount,
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        query
    } = useSelector(selectEmployee)

    const {
        syncLogs
    } = useSelector(selectSyncLog)

    const { screens } = useSelector(selectPermissions)

    // Creating and editing consultants is gated on its own screen.
    const canManageConsultants = useMemo(
        () => screens.some((s: Screen) => s.code === SCREENS.HR_CONSULTANTS),
        [screens]
    )

    const provinces: SelectOption[] = useMemo(() => {
        const p = officeLocations.map(o => o.address?.region?.name)
            .filter((p): p is string => !!p && p.trim() !== '');
        const set = new Set(p);
        return Array.from(set).map(p => ({ label: p, value: p }));
    }, [officeLocations])

    const statusOptions: SelectOption[] = useMemo(() => {
        return status.map(p => ({ label: capitalize(p.description), value: p.id }));
    }, [status])

    const fetchEmployees = async (q: IEmployeeQueryParams) => {
        dispatch(setIsLoadingEmployees(true))
        const emps = await getEmployees(q)

        if (!emps)
            return
        dispatch(setEmployees(emps.data))
        dispatch(setEmployeeCount(emps.count))
        dispatch(setIsLoadingEmployees(false))
    }

    const handleChangeQuery = <K extends keyof IEmployeeQueryParams>(
        key: K,
        value: IEmployeeQueryParams[K]
    ) => {

        let q = { ...query };

        if (key !== 'page' && key !== 'limit') {
            q.page = 0;
            q.limit = Number.MAX_SAFE_INTEGER
        }

        q = {
            ...q,
            [key]: value
        }
        // Filters only change the query; the Search button (or Enter) runs it
        dispatch(setEmployeeQuery(q))
    };

    const handleSearch = () => fetchEmployees(query)

    const handleCloseCreateEmployeeModal = () => setShowCreateEmployeeModal(false)
    const handleShowCreateEmployeeModal = () => setShowCreateEmployeeModal(true)


    const handleOpenDeleteConsultantFormModal = (employee: Employee) => {
        setFocusedEmployee(employee)
        setShowDeleteConsultantModel(true);
    }

    const handleCloseDeleteConsultantModal = async () => {
        setFocusedEmployee(null)
        setShowDeleteConsultantModel(false)
    }

    const handleDeleteConsultant = async (consultant: Employee) => {
        setIsDeletingEmployee(true)
        const res = await deleteConsultant(consultant)
        setShowDeleteConsultantModel(false);

        // Success
        if (res) {
            dispatch(setEmployees(employees.filter(e => e.id !== consultant.id)))
            dispatch(setEmployeeCount(employeeCount - 1))
            setShowDeleteConsultantModel(false)
            setFocusedEmployee(null)
        }

        setIsDeletingEmployee(false)
    }

    const initializePage = async () => {
        let initialQuery = query;

        if (
            jobTitles.length === 0 ||
            departments.length === 0 ||
            officeLocations.length === 0 ||
            employeeTypes.length === 0
        ) {
            const searchFormValues = await dispatch(getEmployeeFormFieldValues())

            if (searchFormValues.status && employees.length === 0) {
                initialQuery = getDefaultQuery(searchFormValues.status)

            }
        }

        if (!syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE])
            await dispatch(getMostRecentSyncLog(SyncLogTypes.EMPLOYEE_DAYFORCE))

        if (employees.length === 0) {
            fetchEmployees(initialQuery)
        }

        dispatch(setEmployeeQuery(initialQuery))
    }


    // Load employees
    useEffect(() => {
        initializePage();
    }, [])

    const handleClickEmployee = (emp: Employee) => {
        navigateToEmployeeDetails(props.router.navigate, emp.id)
    }

    const hanldeEditConsultantForm = (consultant: Employee) => navigateToEditConsultants(props.router.navigate, consultant.id)


    // Edit / delete live on the employee's details page only
    const columns = React.useMemo<ColumnDef<Employee>[]>(() => employeeBaseColumns({
        onCodeClick: handleClickEmployee
    }), []);

    const handleClearSearch = useCallback(() => {
        const defaultQuery = getDefaultQuery(status);
        dispatch(setEmployeeQuery(defaultQuery));
        fetchEmployees(defaultQuery)
    }, [setEmployeeQuery, getEmployees, status]);


    // This just opens the are you sure modal.
    const handleOpenAreYouSureModal = () => {
        dispatch(setShowSyncModal({ show: true }))
    }

    const handleAreYouSureDecline = async () => {
        dispatch(setShowSyncModal({ show: false }))
    }

    const onModalClose = () => {
        dispatch(setShowSyncModal({ show: false }))
    }

    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.EMPLOYEE_DAYFORCE,
        start: syncDayforceData,
        onFinished: () => fetchEmployees(query),
    })

    const handleSyncData = async () => {
        dispatch(setShowSyncModal({ show: false }))
        await startSync()
    }

    const handleAddEmployee = () => {
        window.open("https://www.dayforcehcm.com/mydayforce/login.aspx", "_blank")
        handleCloseCreateEmployeeModal()
    }

    const hanldeAddConsultant = () => navigateToCreateConsultants(props.router.navigate)

    const table = useDataTable({
        data: employees,
        columns,
        initialSorting: [{ id: 'name', desc: false }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    });


    const handleExportToCSV = useCallback(async () => {
        setIsExportingData(true)
        try {
            await downloadEmployeesCsv(query)
        } finally {
            setIsExportingData(false)
        }
    }, [query])

    return <DataTableTemplate
        fullExport={handleExportToCSV}
        title={"Human Resources"}
        breadcrumbItem={"Employees"}
        table={table}
        columns={columns}
        isLoading={isLoadingEmployees}
        rowCount={employeeCount}
        filters={
            <SearchEmployeesForm
                jobTitles={[{ value: '', label: 'All' }, ...jobTitles.map(jt => ({ value: jt.id, label: jt.description }))]}
                managers={[{ value: '', label: 'All' }, ...managers.map(e => ({ value: e.id ?? '', label: e.firstName + " " + e.lastName }))]}
                departments={[{ value: '', label: 'All' }, ...toDepartmentOptions(departments)]}
                employeeTypes={[{ value: '', label: 'All' }, ...employeeTypes.map(e => ({ value: e.id, label: e.name }))]}
                provinces={[{ value: '', label: 'All' }, ...provinces]}
                status={[{ value: '', label: 'All' }, ...statusOptions]}
                handleClearSearch={handleClearSearch}
                handleSearch={handleSearch}
                isSearching={isLoadingEmployees}
                handleChangeQuery={handleChangeQuery}
                query={query}
            />
        }
        actions={[
            <SyncButton
                id="projectsSyncBtn"
                isSyncing={isSyncingData}
                syncLog={syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE]}
                onClick={handleOpenAreYouSureModal}
            />,

            <ActionButton
                onClick={handleShowCreateEmployeeModal}
            >
                Add <PlusIcon />
            </ActionButton>,
            <ActionButton
                onClick={() => navigateToManual(props.router.navigate, "human-resources/employees")}
            >
                Help <HelpIcon />
            </ActionButton>

        ]}

        modal={
            <>
                <AreYouSureModal
                    isOpen={showSyncModal}
                    onModalClose={onModalClose}
                    modalTitle={'Sync Employees'}
                    subTitle={"Sync dayfroce and IbexIQ employees?"}
                    handleAreYouSureAccept={handleSyncData}
                    handleAreYouSureDecline={handleAreYouSureDecline}
                    areYouSureacceptText={isSyncingData ? 'Syncing...' : "Sync"}
                    areYouSuredeclineText={"Cancel"}
                />
                <Modal
                    isOpen={showCreateEmployeeModal}
                    onClose={handleCloseCreateEmployeeModal}
                    title={"Create employee"}
                    subtitle="Select employee or consultant"
                >
                    <div className="d-flex gap-2">
                        <SecondaryButton onClick={handleAddEmployee} className="w-100">Create Employee</SecondaryButton>
                        {canManageConsultants && <PrimaryButton onClick={hanldeAddConsultant} className="w-100">Create Consultant</PrimaryButton>}
                    </div>

                </Modal>
                {
                    focusedEmployee &&
                    <AreYouSureModal
                        isOpen={showDeleteConsultantModal}
                        onModalClose={handleCloseDeleteConsultantModal}
                        modalTitle={'Delete Consultant'}
                        subTitle={"Delete consultant from Ibex."}
                        handleAreYouSureAccept={() => handleDeleteConsultant(focusedEmployee)}
                        handleAreYouSureDecline={handleCloseDeleteConsultantModal}
                        areYouSureacceptText={isDeletingEmployee ? 'Deleting....' : "Delete"}
                        areYouSuredeclineText={"Cancel"}
                    />
                }
            </>
        }
    />
}


export default withRouter(EmployeeListPage);
